import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Question } from "@/components/communication/survey/types";

interface CascadingState {
  classOptions: string[] | null;
  moduleOptions: string[] | null;
  loadingClasses: boolean;
  loadingModules: boolean;
  semesterQuestionId: string | undefined;
  classQuestionId: string | undefined;
  moduleQuestionId: string | undefined;
  getChoiceOptions: (options: any, questionId?: string) => string[];
  isQuestionLoading: (questionId: string) => boolean;
  isWaitingForParent: (questionId: string) => boolean;
  getPlaceholderHint: (questionId: string) => string | null;
}

const findBySource = (questions: Question[], source: string) =>
  questions.find(q => typeof q.options === "object" && !Array.isArray(q.options) && q.options?.source === source)?.id;

export const useCascadingFilters = (
  questions: Question[],
  answers: Record<string, any>,
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, any>>>,
): CascadingState => {
  const [classOptions, setClassOptions] = useState<string[] | null>(null);
  const [moduleOptions, setModuleOptions] = useState<string[] | null>(null);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);

  const semesterQuestionId = useMemo(() => findBySource(questions, "semester"), [questions]);
  const classQuestionId = useMemo(() => findBySource(questions, "class"), [questions]);
  const moduleQuestionId = useMemo(() => findBySource(questions, "module"), [questions]);

  const selectedSemester = semesterQuestionId ? (answers[semesterQuestionId] as string) : undefined;
  const selectedClasses: string[] = classQuestionId
    ? (Array.isArray(answers[classQuestionId])
        ? answers[classQuestionId] as string[]
        : answers[classQuestionId] ? [answers[classQuestionId] as string] : [])
    : [];

  // Cascade: semester → classes
  useEffect(() => {
    if (!selectedSemester || !classQuestionId) {
      setClassOptions(null);
      return;
    }
    setLoadingClasses(true);
    (async () => {
      const { data } = await supabase
        .from("modules_public" as any)
        .select("class")
        .eq("semester", selectedSemester) as { data: { class: string }[] | null };
      if (data) {
        setClassOptions([...new Set(data.map(r => r.class))].sort());
      }
      setLoadingClasses(false);
    })();
    setAnswers(prev => {
      const next = { ...prev };
      if (classQuestionId) delete next[classQuestionId];
      if (moduleQuestionId) delete next[moduleQuestionId];
      return next;
    });
    setModuleOptions(null);
  }, [selectedSemester]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascade: classes → modules
  const classesKey = JSON.stringify(selectedClasses);
  useEffect(() => {
    if (selectedClasses.length === 0 || !selectedSemester || !moduleQuestionId) {
      setModuleOptions(null);
      return;
    }
    setLoadingModules(true);
    (async () => {
      const { data } = await supabase
        .from("modules_public" as any)
        .select("module_name")
        .eq("semester", selectedSemester)
        .in("class", selectedClasses) as { data: { module_name: string }[] | null };
      if (data) {
        setModuleOptions([...new Set(data.map(r => r.module_name))].sort());
      }
      setLoadingModules(false);
    })();
    setAnswers(prev => {
      const next = { ...prev };
      if (moduleQuestionId) delete next[moduleQuestionId];
      return next;
    });
  }, [classesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const getChoiceOptions = useCallback((options: any, questionId?: string): string[] => {
    if (questionId === classQuestionId && classOptions) return classOptions;
    if (questionId === moduleQuestionId && moduleOptions) return moduleOptions;
    if (Array.isArray(options)) return options;
    if (typeof options === "object" && options?.values && Array.isArray(options.values)) return options.values;
    return [];
  }, [classQuestionId, moduleQuestionId, classOptions, moduleOptions]);

  const isQuestionLoading = useCallback((questionId: string) =>
    (questionId === classQuestionId && loadingClasses) ||
    (questionId === moduleQuestionId && loadingModules),
    [classQuestionId, moduleQuestionId, loadingClasses, loadingModules]);

  const isWaitingForParent = useCallback((questionId: string) => {
    if (questionId === classQuestionId && !selectedSemester) return true;
    if (questionId === moduleQuestionId && selectedClasses.length === 0) return true;
    return false;
  }, [classQuestionId, moduleQuestionId, selectedSemester, selectedClasses.length]);

  const getPlaceholderHint = useCallback((questionId: string): string | null => {
    if (questionId === classQuestionId && !selectedSemester) return "Select a semester first";
    if (questionId === moduleQuestionId && selectedClasses.length === 0) return "Select a class first";
    if (isQuestionLoading(questionId)) return "Loading...";
    return null;
  }, [classQuestionId, moduleQuestionId, selectedSemester, selectedClasses.length, isQuestionLoading]);

  return {
    classOptions, moduleOptions, loadingClasses, loadingModules,
    semesterQuestionId, classQuestionId, moduleQuestionId,
    getChoiceOptions, isQuestionLoading, isWaitingForParent, getPlaceholderHint,
  };
};
